import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Wallet } from "lucide-react";
import SEO from "@/components/SEO";

export default function Register() {
  const { setUser, user } = useAuth();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  React.useEffect(() => {
    if (user && user !== false) navigate("/workspace");
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/register", { name, email, password });
      setUser(data.user);
      toast.success("Welcome to GEMS");
      navigate("/new");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6 relative overflow-hidden">
      <SEO
        title="Create Account — GEMS"
        description="Register a free account on GEMS to create group expense ledgers and settle payments with your crew."
        canonicalUrl="https://splitly.app/register"
      />
      <div className="absolute -top-40 -right-40 w-80 h-80 rounded-full bg-emerald-100 blur-3xl opacity-60" />
      <div className="absolute -bottom-40 -left-40 w-80 h-80 rounded-full bg-rose-100 blur-3xl opacity-60" />

      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md relative z-10"
      >
        <div className="mb-8 text-center">
          <Link to="/" className="inline-flex flex-col items-center gap-2 group cursor-pointer" data-testid="register-logo-link">
            <div className="w-11 h-11 rounded-2xl bg-slate-900 grid place-items-center group-hover:scale-105 transition-transform">
              <Wallet className="w-5 h-5 text-emerald-400" />
            </div>
            <h1 className="heading text-4xl font-bold text-slate-900 tracking-tight group-hover:text-slate-800 transition-colors">Create your account</h1>
          </Link>
          <p className="text-slate-500 mt-2">Split expenses with your crew in seconds.</p>
        </div>

        <form
          onSubmit={submit}
          className="bg-white rounded-3xl p-8 card-soft space-y-5 border border-slate-200/60"
          data-testid="register-form"
        >
          <div>
            <Label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Full name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} className="mt-2 h-12 rounded-2xl" required data-testid="register-name-input" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Email</Label>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-2 h-12 rounded-2xl" required data-testid="register-email-input" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Password</Label>
            <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} minLength={6} className="mt-2 h-12 rounded-2xl" required data-testid="register-password-input" />
          </div>
          <Button
            type="submit"
            disabled={busy}
            className="w-full h-12 rounded-full bg-slate-900 hover:bg-slate-800 text-white font-semibold"
            data-testid="register-submit-button"
          >
            {busy ? "Creating…" : "Create account"}
          </Button>
          <div className="text-center text-sm text-slate-500">
            Already have an account?{" "}
            <Link to="/login" className="text-slate-900 font-semibold underline underline-offset-2">Sign in</Link>
          </div>
        </form>
      </motion.div>
    </div>
  );
}
